import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { carregarExterno } from '@/lib/widgets/widget-loader';
import type { AdPlacement } from '@/lib/services/store.service';

const SCRIPT = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
let carregadoPara: string | null = null;

/** O `adsbygoogle.js` entra uma vez por página, só depois do aceite (quem chama já conferiu). */
function garantirScript(cliente: string) {
  if (carregadoPara === cliente) return;
  carregadoPara = cliente;
  carregarExterno(`${SCRIPT}?client=${encodeURIComponent(cliente)}`, { crossorigin: 'anonymous' });
}

interface AnuncioGoogleProps {
  cliente: string;
  bloco: string;
  posicao: AdPlacement;
  /** O Google não preencheu o espaço: quem chamou troca pelo anúncio próprio ou some. */
  aoNaoPreencher: () => void;
}

/**
 * Um bloco manual do AdSense. O `<ins>` é criado pelo React com atributos (nada
 * de `innerHTML`); o `push({})` pede o anúncio. O rótulo segue a regra do
 * Google: "Publicidade" (ou "Anúncios"), nunca outro texto.
 */
export function AnuncioGoogle({ cliente, bloco, posicao, aoNaoPreencher }: AnuncioGoogleProps) {
  const { t } = useTranslation();
  const ins = useRef<HTMLModElement>(null);
  const avisar = useRef(aoNaoPreencher);
  avisar.current = aoNaoPreencher;

  useEffect(() => {
    garantirScript(cliente);
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      // Bloqueador de anúncio ou script fora: o observador abaixo não dispara e o espaço fica vazio.
    }
    const el = ins.current;
    if (!el) return;
    const observador = new MutationObserver(() => {
      if (el.getAttribute('data-ad-status') === 'unfilled') avisar.current();
    });
    observador.observe(el, { attributes: true, attributeFilter: ['data-ad-status'] });
    return () => observador.disconnect();
  }, [cliente, bloco]);

  return (
    <aside aria-label={t('store.ads.label_google')} data-anuncio-google={posicao} className="overflow-hidden rounded-xl border border-border bg-card p-2">
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t('store.ads.label_google')}</p>
      <ins
        ref={ins}
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={cliente}
        data-ad-slot={bloco}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </aside>
  );
}
